<script setup lang="ts">
import Modal from '~/components/Common/Modal/Modal.vue'
import Input from '~/components/Common/Form/Input.vue'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'
import { useProfileSelfStore } from '~/stores/user/Self'
import { useProfileStore } from '~/stores/user/Profile'

import type { Profile } from '~/types/profile'
import Button from '~/components/Common/Button/Button.vue'

const props = defineProps<{
	show: boolean
	user: Profile
}>()

const emits = defineEmits(['close', 'success', 'errored'])

const store = useProfileSelfStore()

// Form validation schema
const validationSchema = toTypedSchema(
	zod.object({
		username: zod.string().min(2, 'Please provide a unique username'),
	}),
)

const { handleSubmit, errors } = useForm({
	validationSchema,
})

const { value: username } = useField('username')
const usernameError = ref<string | undefined>(undefined)

const onSubmit = handleSubmit(async () => {
	if (!props.user.id) {
		console.error('User is required')
		emits('errored', 'User is required')
		return
	}

	//do unique username check
	const profileStore = useProfileStore(username.value as string)
	const profile = await profileStore().getByUsername()

	if (profile?.id) {
		usernameError.value = 'Username is already taken'
		console.error('Username is already taken')
		return
	}

	try {
		await store().setUsername(username.value as string)
		emits('success')
	} catch (err: any) {
		console.error(err)
		emits('errored', err.message || 'An unexpected error occurred')
	}
})

watch(
	() => username.value,
	username => {
		if (username) {
			usernameError.value = undefined
		}
	},
)
</script>

<template>

	<Modal
		:show="show"
		title="Choose Community Username"
		class="max-h-fit"
		boxClass="w-full sm:max-w-xl"
		@close="emits('close')">

		<form class="space-y-4" @submit.prevent="onSubmit">

			<Input
				v-model="username"
				placeholder="Choose a unique username"
				label="Username"
				:error="errors.username || usernameError"
			/>

				<div class="flex">
					<Button type="submit">
						Submit
					</Button>
				</div>
			</form>
	</Modal>
</template>
